import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import {
  canViewPage,
  hasAllPermissions,
  hasAnyPermission,
  isAdminUser,
} from '@/lib/hrm-permissions';

export interface UsePermissions {
  /** Canonical permission codes granted to the current user for HRM. */
  permissions: string[];
  /** Org Admin / super admin — bypasses all gating (false while role-previewing). */
  isAdmin: boolean;
  /** True when viewing as another org role (UI + API session preview). */
  isRolePreview: boolean;
  /** Preview label when active, else null. */
  rolePreviewLabel: string | null;
  /** True when the user holds the exact canonical permission code. */
  has: (code: string) => boolean;
  /** Alias for has() */
  hasPermission: (code: string) => boolean;
  /** True when the user holds any of the listed codes (admins always true). */
  hasAny: (codes: string[]) => boolean;
  /** True when the user holds every listed code (admins always true). */
  hasAll: (codes: string[]) => boolean;
  /** True when the user may open the given navigation page key. */
  canView: (pageKey: string) => boolean;

  // Action helpers (module.resource.action)
  canRead: (module: string) => boolean;
  canCreate: (module: string) => boolean;
  canUpdate: (module: string) => boolean;
  canDelete: (module: string) => boolean;
  canImport: (module: string) => boolean;
  canExport: (module: string) => boolean;
}

/**
 * Permission gate driven by codes from GET /auth/me/permissions (stored in auth-store).
 * When `rolePreview` is set, UI gates use that role's permissions (synced to backend).
 */
export function usePermissions(): UsePermissions {
  const displayUser = useAuthStore((s) => s.displayUser);
  const rolePreview = useAuthStore((s) => s.rolePreview);

  return useMemo(() => {
    const isRolePreview = Boolean(rolePreview);
    const permissions = rolePreview?.permissions ?? displayUser?.permissions ?? [];
    // Never bypass gates while previewing — enforce exact role permission codes only.
    const isAdmin = isRolePreview
      ? false
      : isAdminUser({
          permissions,
          isSuperAdmin: displayUser?.isSuperAdmin,
          authRole: displayUser?.authRole,
        });
    const set = new Set(permissions.map((p) => p.trim().toLowerCase()));

    const has = (code: string) => isAdmin || set.has(code.trim().toLowerCase());

    const rolePreviewLabel = rolePreview
      ? [rolePreview.roleName, rolePreview.levelName].filter(Boolean).join(' · ')
      : null;

    return {
      permissions,
      isAdmin,
      isRolePreview,
      rolePreviewLabel,
      has,
      hasPermission: has,
      hasAny: (codes: string[]) => isAdmin || hasAnyPermission(set, codes),
      hasAll: (codes: string[]) => isAdmin || hasAllPermissions(set, codes),
      canView: (pageKey: string) => canViewPage(pageKey, { permissions, isAdmin }),

      canRead: (module: string) => has(`${module}.read`),
      canCreate: (module: string) => has(`${module}.create`),
      canUpdate: (module: string) => has(`${module}.update`),
      canDelete: (module: string) => has(`${module}.delete`),
      canImport: (module: string) => has(`${module}.import`),
      canExport: (module: string) => has(`${module}.export`),
    };
  }, [displayUser, rolePreview]);
}
